import {
  AiPhone01Icon,
  AlertDiamondIcon,
  BookOpen01Icon,
  Call02Icon,
  Building06Icon,
  CalendarCheckIn01Icon,
  CallRinging02Icon,
  ChartColumnIcon,
  CustomerService01Icon,
  DashboardSquare01Icon,
  File02Icon,
  Home01Icon,
  InboxIcon,
  Megaphone01Icon,
  Plug01Icon,
  Settings02Icon,
  ShieldCheckIcon,
  TaskDaily01Icon,
  TaskDone01Icon,
  TestTube01Icon,
  UserGroupIcon,
  UserSearch01Icon,
  WhatsappIcon,
  WorkflowSquare01Icon,
} from '@hugeicons/core-free-icons';
import { MODULE_DEFAULT_ENABLED, MODULE_LABELS, roleAtLeast, type PlatformCapability, type ProductModule, type Role } from '@lia/contracts';
import type { IconData } from '@lia/ui';

/** A rail column entry: the shared sections, a product module, or platform administration. */
export type RailKey = 'general' | ProductModule | 'platform';

export interface RailModule {
  key: RailKey;
  /** Full name (accessible name, section-nav heading). */
  label: string;
  /** Visible label under the rail icon. */
  shortLabel: string;
  icon: IconData;
  /** Landing path relative to /w/:workspaceId, or absolute when `absolute`. */
  landing: string;
  absolute?: boolean;
  capabilities?: readonly PlatformCapability[];
}

export const RAIL_MODULES: readonly RailModule[] = [
  { key: 'general', label: 'General', shortLabel: 'General', icon: DashboardSquare01Icon, landing: 'inicio' },
  { key: 'whatsapp', label: MODULE_LABELS.whatsapp, shortLabel: 'WhatsApp', icon: WhatsappIcon, landing: 'whatsapp' },
  { key: 'ai_calls', label: MODULE_LABELS.ai_calls, shortLabel: 'Llamadas IA', icon: AiPhone01Icon, landing: 'ai-calls' },
  { key: 'copilot', label: MODULE_LABELS.copilot, shortLabel: 'Copiloto', icon: CustomerService01Icon, landing: 'copilot' },
  {
    key: 'platform',
    label: 'Administración de plataforma',
    shortLabel: 'Plataforma',
    icon: ShieldCheckIcon,
    landing: '/plataforma/instrucciones',
    absolute: true,
    capabilities: ['platform_prompt_admin', 'platform_model_admin', 'platform_integration_admin'],
  },
];

/** URL segment of each product module under /w/:workspaceId. */
export const MODULE_SEGMENT: Record<ProductModule, string> = { whatsapp: 'whatsapp', ai_calls: 'ai-calls', copilot: 'copilot' };

export interface NavEntry {
  key: string;
  label: string;
  /** Path relative to /w/:workspaceId, or absolute when `absolute`. */
  path: string;
  icon: IconData;
  module: RailKey;
  /** Minimum workspace role to see the item. */
  minRole?: Role;
  /** Platform capabilities (not workspace-scoped): the item is visible when the user holds ANY of them. */
  capabilities?: readonly PlatformCapability[];
  absolute?: boolean;
  /** Active only on an exact path match (module landings), not for sub-paths. */
  exact?: boolean;
}

// Visibility mirrors the API's minimum roles; the API still enforces every request.
export const NAV_ENTRIES: readonly NavEntry[] = [
  // General (addendum §2): shared sections keep their Spanish paths.
  { key: 'inicio', label: 'Inicio', path: 'inicio', icon: Home01Icon, module: 'general', minRole: 'viewer' },
  { key: 'mi-trabajo', label: 'Mi trabajo', path: 'mi-trabajo', icon: TaskDaily01Icon, module: 'general', minRole: 'advisor' },
  // Plan B7: advisor calls on Telnyx — own history for advisors; team history, presence and reports for managers.
  { key: 'mis-llamadas', label: 'Mis llamadas', path: 'mis-llamadas', icon: Call02Icon, module: 'general', minRole: 'advisor' },
  { key: 'prospectos', label: 'Prospectos', path: 'prospectos', icon: UserSearch01Icon, module: 'general', minRole: 'advisor' },
  { key: 'citas', label: 'Citas', path: 'citas', icon: CalendarCheckIn01Icon, module: 'general', minRole: 'advisor' },
  // Setter §05.19, §05.20: incidents with a named owner (advisors see theirs; management, the team's).
  { key: 'incidencias', label: 'Incidencias', path: 'incidencias', icon: AlertDiamondIcon, module: 'general', minRole: 'advisor' },
  { key: 'proyectos', label: 'Proyectos', path: 'proyectos', icon: Building06Icon, module: 'general', minRole: 'viewer' },
  { key: 'conocimiento', label: 'Conocimiento', path: 'conocimiento', icon: BookOpen01Icon, module: 'general', minRole: 'manager' },
  { key: 'integraciones', label: 'Integraciones', path: 'integraciones', icon: Plug01Icon, module: 'general', minRole: 'manager' },
  { key: 'equipo', label: 'Equipo', path: 'equipo', icon: UserGroupIcon, module: 'general', minRole: 'manager' },
  // Setter §10.8/§10.9: AI kill switch, controlled resume, incidents and alerts.
  { key: 'contingencia', label: 'Contingencia', path: 'contingencia', icon: AlertDiamondIcon, module: 'general', minRole: 'manager' },
  // Setter §06.10.3–4: contacts the AI flagged for possible abuse; a person confirms or dismisses.
  { key: 'revision-abuso', label: 'Revisión de abuso', path: 'revision-abuso', icon: AlertDiamondIcon, module: 'general', minRole: 'manager' },
  { key: 'asesores', label: 'Asesores', path: 'asesores', icon: TaskDone01Icon, module: 'general', minRole: 'manager' },
  { key: 'reportes', label: 'Reportes', path: 'reportes', icon: ChartColumnIcon, module: 'general', minRole: 'viewer' },
  { key: 'configuracion', label: 'Configuración', path: 'configuracion', icon: Settings02Icon, module: 'general', minRole: 'viewer' },
  // Agente WhatsApp
  { key: 'whatsapp', label: 'Resumen', path: 'whatsapp', icon: DashboardSquare01Icon, module: 'whatsapp', minRole: 'viewer', exact: true },
  { key: 'whatsapp-inbox', label: 'Bandeja', path: 'whatsapp/inbox', icon: InboxIcon, module: 'whatsapp', minRole: 'advisor' },
  { key: 'whatsapp-automations', label: 'Automatizaciones', path: 'whatsapp/automations', icon: WorkflowSquare01Icon, module: 'whatsapp', minRole: 'manager' },
  // PRD §13: synced templates of every WhatsApp connection (read-only here; created in WhatsApp Manager / Kapso).
  { key: 'whatsapp-templates', label: 'Plantillas', path: 'whatsapp/templates', icon: File02Icon, module: 'whatsapp', minRole: 'manager' },
  { key: 'whatsapp-campaigns', label: 'Campañas', path: 'whatsapp/campaigns', icon: Megaphone01Icon, module: 'whatsapp', minRole: 'manager' },
  { key: 'whatsapp-settings', label: 'Configuración', path: 'whatsapp/settings', icon: Settings02Icon, module: 'whatsapp', minRole: 'manager' },
  { key: 'whatsapp-reports', label: 'Reportes', path: 'whatsapp/reports', icon: ChartColumnIcon, module: 'whatsapp', minRole: 'viewer' },
  // Agente de llamadas IA
  { key: 'ai-calls', label: 'Resumen', path: 'ai-calls', icon: DashboardSquare01Icon, module: 'ai_calls', minRole: 'viewer', exact: true },
  { key: 'ai-calls-calls', label: 'Llamadas', path: 'ai-calls/calls', icon: CallRinging02Icon, module: 'ai_calls', minRole: 'advisor' },
  // Setter §06: capacity + priority queue of AI calls, committed-time tasks, first-attempt latency.
  { key: 'ai-calls-queue', label: 'Cola de llamadas', path: 'ai-calls/queue', icon: TaskDaily01Icon, module: 'ai_calls', minRole: 'manager' },
  { key: 'ai-calls-settings', label: 'Configuración', path: 'ai-calls/settings', icon: Settings02Icon, module: 'ai_calls', minRole: 'manager' },
  { key: 'ai-calls-test', label: 'Probar', path: 'ai-calls/test', icon: TestTube01Icon, module: 'ai_calls', minRole: 'manager' },
  { key: 'ai-calls-reports', label: 'Reportes', path: 'ai-calls/reports', icon: ChartColumnIcon, module: 'ai_calls', minRole: 'viewer' },
  // Copiloto de llamadas
  { key: 'copilot', label: 'Resumen', path: 'copilot', icon: DashboardSquare01Icon, module: 'copilot', minRole: 'viewer', exact: true },
  { key: 'copilot-sessions', label: 'Sesiones', path: 'copilot/sessions', icon: CallRinging02Icon, module: 'copilot', minRole: 'advisor' },
  { key: 'copilot-playbooks', label: 'Playbooks', path: 'copilot/playbooks', icon: BookOpen01Icon, module: 'copilot', minRole: 'manager' },
  { key: 'copilot-settings', label: 'Configuración', path: 'copilot/settings', icon: Settings02Icon, module: 'copilot', minRole: 'admin' },
  { key: 'copilot-reports', label: 'Reportes', path: 'copilot/reports', icon: ChartColumnIcon, module: 'copilot', minRole: 'viewer' },
  // Estateflow operators
  {
    key: 'plataforma',
    label: 'Administración de plataforma',
    path: '/plataforma/instrucciones',
    icon: ShieldCheckIcon,
    module: 'platform',
    capabilities: ['platform_prompt_admin', 'platform_model_admin', 'platform_integration_admin'],
    absolute: true,
  },
];

/** Items visible for a role (null when the user has no membership in the current workspace) and capabilities. */
export function visibleNav(role: Role | null, capabilities: readonly PlatformCapability[], entries: readonly NavEntry[] = NAV_ENTRIES): NavEntry[] {
  return entries.filter((e) => {
    if (e.capabilities && !e.capabilities.some((c) => capabilities.includes(c))) return false;
    if (e.minRole && (!role || !roleAtLeast(role, e.minRole))) return false;
    return true;
  });
}

/** Section nav of one rail module. */
export function moduleNav(module: RailKey, role: Role | null, capabilities: readonly PlatformCapability[]): NavEntry[] {
  return visibleNav(role, capabilities).filter((e) => e.module === module);
}

export type ModuleEnablement = Record<ProductModule, boolean>;

export interface RailItem {
  module: RailModule;
  /** Not enabled for the workspace: only admins see it (with an explanation and an enable action). */
  paused: boolean;
}

const isProductModule = (k: RailKey): k is ProductModule => k !== 'general' && k !== 'platform';

/**
 * Rail entries for a role. Disabled modules are visible to admins (marked paused) and hidden from everyone else
 * (addendum §1). `enabled` null = state not loaded yet → module defaults.
 */
export function visibleRail(role: Role | null, capabilities: readonly PlatformCapability[], enabled: ModuleEnablement | null): RailItem[] {
  const states = enabled ?? MODULE_DEFAULT_ENABLED;
  const isAdmin = !!role && roleAtLeast(role, 'admin');
  const out: RailItem[] = [];
  for (const m of RAIL_MODULES) {
    if (m.capabilities) {
      if (m.capabilities.some((c) => capabilities.includes(c))) out.push({ module: m, paused: false });
      continue;
    }
    if (!role) continue;
    if (isProductModule(m.key)) {
      const on = states[m.key];
      if (!on && !isAdmin) continue;
      out.push({ module: m, paused: !on });
      continue;
    }
    out.push({ module: m, paused: false });
  }
  return out;
}

/** Workspace-relative rest of a path ("whatsapp/inbox/c1"), or null outside /w/:id. */
function workspaceRest(pathname: string): string | null {
  const m = /^\/w\/[^/]+\/?(.*)$/.exec(pathname);
  return m ? m[1]!.replace(/\/+$/, '') : null;
}

export function railKeyFromPath(pathname: string): RailKey | null {
  if (pathname.startsWith('/plataforma')) return 'platform';
  const rest = workspaceRest(pathname);
  if (rest === null) return null;
  const first = rest.split('/')[0];
  for (const [mod, seg] of Object.entries(MODULE_SEGMENT) as [ProductModule, string][]) if (first === seg) return mod;
  return 'general';
}

/** Key of the section to mark as current: the most specific matching entry. */
export function activeNavKey(pathname: string): string | null {
  if (pathname.startsWith('/plataforma')) return 'plataforma';
  const rest = workspaceRest(pathname);
  if (rest === null) return null;
  let best: NavEntry | null = null;
  for (const e of NAV_ENTRIES) {
    if (e.absolute) continue;
    const match = e.exact ? rest === e.path : rest === e.path || rest.startsWith(`${e.path}/`);
    if (match && (!best || e.path.length > best.path.length)) best = e;
  }
  if (!best && /^whatsapp\/test(\/|$)/.test(rest)) return 'whatsapp-settings';
  return best?.key ?? null;
}

// Old module paths (before the three-module navigation) → new ones. The rest of the path is kept.
const LEGACY_PREFIXES: readonly (readonly [string, string])[] = [
  ['agentes/probar', 'whatsapp/test'],
  ['agentes', 'whatsapp/settings'],
  ['conversaciones', 'whatsapp/inbox'],
  ['llamadas', 'ai-calls/calls'],
  ['automatizaciones', 'whatsapp/automations'],
  ['campanas', 'whatsapp/campaigns'],
];

/** New workspace-relative path for an old one, or null when the path did not move. */
export function legacyRedirect(rest: string): string | null {
  for (const [from, to] of LEGACY_PREFIXES) {
    if (rest === from) return to;
    if (rest.startsWith(`${from}/`)) return `${to}${rest.slice(from.length)}`;
  }
  return null;
}

/** Link target for a nav entry, keeping the project filter across sections. */
export function navHref(entry: Pick<NavEntry, 'path' | 'absolute' | 'capabilities'>, workspaceId: string | null, projectId: string | null): string {
  if (entry.absolute) return entry.path;
  const base = workspaceId ? `/w/${workspaceId}/${entry.path}` : '/';
  return projectId && !entry.capabilities ? `${base}?proyecto=${encodeURIComponent(projectId)}` : base;
}

/** Link target for a rail module (its landing), keeping the project filter. */
export function railHref(m: RailModule, workspaceId: string | null, projectId: string | null): string {
  return navHref({ path: m.landing, absolute: m.absolute, capabilities: m.capabilities }, workspaceId, projectId);
}
